import React from 'react';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';

const codeBlock = (code) => (
  <pre className="bg-gray-100 rounded p-2 text-xs overflow-x-auto mb-2"><code>{code}</code></pre>
);

const Autodialer = () => (
  <div className="min-h-screen bg-[#f6fbfa]">
    <Navbar />
    <Sidebar />
    <main className="lg:pl-32 pt-24 flex justify-center">
      <div className="w-full max-w-screen-md px-4 pb-12">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">🎯 Taar.AI Autodialer — Client Integration</h1>
        
        <p className="mb-4">The Autodialer allows you to define <strong>Targets</strong> (a callee + multiple callers) and dial all callers simultaneously. Whoever answers first is connected back to the agent.</p>

        <h2 className="text-lg font-semibold text-gray-900 mt-6 mb-2">⚡ Quick Start</h2>
        <p className="mb-4">Get up and running in 10-15 lines:</p>
        {codeBlock(`// Instantiate client and register message handler
client.onMessage = (message) => {
  switch(message.type) {
    case 'autodialer.answered':
      console.log('Someone answered:', message.callee);
      break;
    case 'call.picked':
      console.log('Connected to agent:', message.callee);
      break;
    case 'call.ended':
      console.log('Call ended:', message.durationSec);
      break;
    case 'autodialer.completed':
      console.log('Final status:', message.finalStatus);
      break;
  }
};

// Start autodialing
const response = await client.autodial({
  targets: [{
    metaId: "lead-98421",
    callee: "+919838643999",
    caller: ["+918068130201", "+918068130202"]
  }]
});

if (response.status === 'ACCEPTED') {
  console.log('Autodial started - waiting for answer...');
}`)}

        <h2 className="text-lg font-semibold text-gray-900 mt-6 mb-2">📋 Request Model</h2>
        <p className="mb-4">The autodial request structure:</p>
        
        <h3 className="font-semibold mt-4 mb-2">Target Fields</h3>
        <ul className="list-disc pl-6 text-sm text-gray-700 mb-4">
          <li><strong>metaId</strong>: Opaque client correlation ID; echoed in all events</li>
          <li><strong>callee</strong>: E.164 number to be called (the target)</li>
          <li><strong>caller</strong>: Array of 1..N E.164 numbers owned by tenant, must have free SIP capacity</li>
        </ul>

        <h3 className="font-semibold mt-4 mb-2">Constraints</h3>
        <ul className="list-disc pl-6 text-sm text-gray-700 mb-4">
          <li><strong>callee</strong>: E.164 format, the number to be called</li>
          <li><strong>caller[]</strong>: E.164 format, 1..N numbers owned by tenant; dialed in parallel</li>
          <li><strong>metaId</strong>: Opaque client correlation ID; echoed in events</li>
        </ul>

        <h3 className="font-semibold mt-4 mb-2">Example Request</h3>
        {codeBlock(`await client.autodial({
  targets: [{
    metaId: "lead-98421",
    callee: "+919838643999",
    caller: ["+918068130201", "+918068130202"]
  }]
});`)}

        <h2 className="text-lg font-semibold text-gray-900 mt-6 mb-2">📤 Response Model</h2>
        <p className="mb-4">Response status and client actions:</p>
        
        <table className="min-w-full text-sm text-left mb-4">
          <thead>
            <tr className="border-b">
              <th className="pr-4 py-2 font-semibold">Status</th>
              <th className="pr-4 py-2 font-semibold">When it happens</th>
              <th className="pr-4 py-2 font-semibold">Client action</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b">
              <td className="pr-4 py-2"><code>ACCEPTED</code></td>
              <td className="pr-4 py-2">Request accepted and dial started/queued</td>
              <td className="pr-4 py-2">Start listening to events</td>
            </tr>
            <tr className="border-b">
              <td className="pr-4 py-2"><code>RATE_LIMIT_EXCEEDED</code></td>
              <td className="pr-4 py-2">Tenant/agent/global rate limit hit</td>
              <td className="pr-4 py-2">Backoff and retry</td>
            </tr>
            <tr className="border-b">
              <td className="pr-4 py-2"><code>MAX_SIP_CONCURRENCY_LIMIT</code></td>
              <td className="pr-4 py-2">SIP channels in use; cannot place more calls now</td>
              <td className="pr-4 py-2">Retry on event or after delay</td>
            </tr>
            <tr className="border-b">
              <td className="pr-4 py-2"><code>TARGET_VALIDATION_FAILED</code></td>
              <td className="pr-4 py-2">Invalid numbers or payload</td>
              <td className="pr-4 py-2">Fix input and retry</td>
            </tr>
            <tr className="border-b">
              <td className="pr-4 py-2"><code>INVALID_CREDENTIALS</code></td>
              <td className="pr-4 py-2">JWT invalid/expired</td>
              <td className="pr-4 py-2">Re-authenticate</td>
            </tr>
            <tr className="border-b">
              <td className="pr-4 py-2"><code>NOT_ALLOWED</code></td>
              <td className="pr-4 py-2">Missing permission</td>
              <td className="pr-4 py-2">Request access</td>
            </tr>
            <tr className="border-b">
              <td className="pr-4 py-2"><code>SERVER_ERROR</code></td>
              <td className="pr-4 py-2">Transient server issue</td>
              <td className="pr-4 py-2">Retry with backoff</td>
            </tr>
            <tr>
              <td className="pr-4 py-2"><code>UNKNOWN</code></td>
              <td className="pr-4 py-2">Fallback</td>
              <td className="pr-4 py-2">Log + escalate</td>
            </tr>
          </tbody>
        </table>

        <h2 className="text-lg font-semibold text-gray-900 mt-6 mb-2">📡 Event Flow (WebSocket onMessage)</h2>
        <p className="mb-4">Document these exact events with minimal payload examples:</p>

        <h3 className="font-semibold mt-4 mb-2">1. autodialer.answered</h3>
        <p className="mb-2">When <strong>any</strong> callee answers (emitted per answering target).</p>
        {codeBlock(`{
  "type": "autodialer.answered",
  "metaId": "lead-98421",
  "caller": "+918068130201",
  "callee": "+919838643999",
  "answeredAt": "<timestamp>"
}`)}

        <h3 className="font-semibold mt-4 mb-2">2. call.picked</h3>
        <p className="mb-2">When the winning callee is <strong>assigned back to the agent</strong> (bridging begins).</p>
        {codeBlock(`{
  "type": "call.picked",
  "metaId": "lead-98421",
  "caller": "+918068130201",
  "callee": "+919838643999",
  "agentId": "agent_42",
  "bridgeId": "brg_01J3Z6…",
  "timestamp": "<timestamp>"
}`)}

        <h3 className="font-semibold mt-4 mb-2">3. call.ended</h3>
        <p className="mb-2">When the bridged call (agent ↔ callee) ends. Contains the usual <code>recordingUrl</code> and <code>disposition</code>.</p>
        {codeBlock(`{
  "type": "call.ended",
  "metaId": "lead-98421",
  "agentId": "agent_42",
  "callee": "+919838643999",
  "durationSec": 312,
  "disposition": "COMPLETED", 
  "recordingUrl": "https://…/recordings/brg_01J3Z6….mp3",
  "endedAt": "<timestamp>"
}`)}

        <h3 className="font-semibold mt-4 mb-2">4. autodialer.completed</h3>
        <p className="mb-2">Final status for all parallel targets (emitted once per <code>metaId</code>/target group). <strong>Fires even if nobody picks.</strong></p>
        {codeBlock(`{
  "namespace": "autodialer.completed",
  "data": {
    "autoDialId": "70ijov7jv0ffuvbam5uo",
    "initiatedAt": 1756896184083,
    "recordingUrl": "https://secure-data.taar.ai/recordings/70ijov7jv0ffuvbam5uo",
    "finalStatus": "NORMAL_CLEARING",
    "attempts": [
      {
        "callee": "09838643999",
        "caller": "08068130201",
        "metaId": "blah123",
        "initiatedAt": 1756896193859,
        "startedAt": 1756896195571,
        "pickedAt": 1756896204973,
        "cancelledAt": 0,
        "completedAt": 1756896214018,
        "recordingUrl": "https://secure-data.taar.ai/recordings/70ijov7jv0ffuvbam5uo",
        "disconnectedBy": "Customer",
        "status": "CLOSED_AFTER_PICKUP"
      }
    ]
  }
}`)}

        <div className="mb-4">
          <h4 className="font-semibold mb-2">Status and DisconnectedBy Reference:</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-300 rounded-lg">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left border-b border-gray-300">DisconnectedBy</th>
                  <th className="px-4 py-2 text-left border-b border-gray-300">State</th>
                  <th className="px-4 py-2 text-left border-b border-gray-300">Reason</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-blue-50"><td className="px-4 py-2 border-b border-gray-200 font-semibold">Agent</td><td className="px-4 py-2 border-b border-gray-200">CancelledBeforePickup</td><td className="px-4 py-2 border-b border-gray-200">Agent cancelled before call was picked up</td></tr>
                <tr className="bg-blue-50"><td className="px-4 py-2 border-b border-gray-200"></td><td className="px-4 py-2 border-b border-gray-200">ClosedAfterPickup</td><td className="px-4 py-2 border-b border-gray-200">Agent ended call after it was picked up</td></tr>
                <tr className="bg-green-50"><td className="px-4 py-2 border-b border-gray-200 font-semibold">AutoDialer</td><td className="px-4 py-2 border-b border-gray-200">CancelledBeforePickup</td><td className="px-4 py-2 border-b border-gray-200">System cancelled before call was picked up</td></tr>
                <tr className="bg-green-50"><td className="px-4 py-2 border-b border-gray-200"></td><td className="px-4 py-2 border-b border-gray-200">ClosedAfterPickup</td><td className="px-4 py-2 border-b border-gray-200">System ended call after it was picked up</td></tr>
                <tr className="bg-green-50"><td className="px-4 py-2 border-b border-gray-200"></td><td className="px-4 py-2 border-b border-gray-200">TechnicalIssue</td><td className="px-4 py-2 border-b border-gray-200">Technical problem with the call</td></tr>
                <tr className="bg-red-50"><td className="px-4 py-2 border-b border-gray-200 font-semibold">Carrier</td><td className="px-4 py-2 border-b border-gray-200">Blocked</td><td className="px-4 py-2 border-b border-gray-200">Number blocked by carrier</td></tr>
                <tr className="bg-red-50"><td className="px-4 py-2 border-b border-gray-200"></td><td className="px-4 py-2 border-b border-gray-200">SwitchedOff</td><td className="px-4 py-2 border-b border-gray-200">Phone was switched off</td></tr>
                <tr className="bg-red-50"><td className="px-4 py-2 border-b border-gray-200"></td><td className="px-4 py-2 border-b border-gray-200">WrongNumber</td><td className="px-4 py-2 border-b border-gray-200">Invalid or wrong phone number</td></tr>
                <tr className="bg-yellow-50"><td className="px-4 py-2 border-b border-gray-200 font-semibold">Customer</td><td className="px-4 py-2 border-b border-gray-200">ClosedAfterPickup</td><td className="px-4 py-2 border-b border-gray-200">Customer ended call after it was picked up</td></tr>
                <tr className="bg-yellow-50"><td className="px-4 py-2 border-b border-gray-200"></td><td className="px-4 py-2 border-b border-gray-200">DeclinedByPhone</td><td className="px-4 py-2 border-b border-gray-200">Customer declined the call on their phone</td></tr>
                <tr className="bg-yellow-50"><td className="px-4 py-2 border-b border-gray-200"></td><td className="px-4 py-2 border-b border-gray-200">NoAnswerTimeExceeded</td><td className="px-4 py-2 border-b border-gray-200">Customer didn't answer within time limit</td></tr>
                <tr className="bg-yellow-50"><td className="px-4 py-2 border-b border-gray-200"></td><td className="px-4 py-2 border-b border-gray-200">RejectedByUser</td><td className="px-4 py-2 border-b border-gray-200">Customer actively rejected the call</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        <p className="mb-4 text-sm text-gray-700">Suggested <code>finalStatus</code> values: <code>CONNECTED</code>, <code>NO_ANSWER</code>, <code>BUSY</code>, <code>FAILED</code>.</p>

        <h2 className="text-lg font-semibold text-gray-900 mt-6 mb-2">🔄 End-to-End Example</h2>
        <p className="mb-4">Complete implementation with UI updates:</p>
        {codeBlock(`// Register message handler with switch mapping
client.onMessage = (message) => {
  switch(message.type) {
    case 'autodialer.answered':
      updateUI('dialing', \`\${message.callee} answered\`);
      break;
      
    case 'call.picked':
      updateUI('connected', \`Connected to \${message.callee}\`);
      break;
      
    case 'call.ended':
      updateUI('ended', \`Call ended (\${message.durationSec}s)\`);
      break;
      
    case 'autodialer.completed':
      if (message.finalStatus !== 'CONNECTED') {
        updateUI('no-answer', 'No one answered');
      }
      break;
  }
};

// Start autodialing
const response = await client.autodial({
  targets: [{
    metaId: "lead-98421",
    callee: "+919838643999",
    caller: ["+918068130201", "+918068130202"]
  }]
});

if (response.status === 'ACCEPTED') {
  updateUI('dialing', 'Dialing music playing...');
}

// Minimal UI update function
function updateUI(status, message) {
  const statusEl = document.getElementById('status');
  const messageEl = document.getElementById('message');
  statusEl.textContent = status;
  messageEl.textContent = message;
}`)}

        <h2 className="text-lg font-semibold text-gray-900 mt-6 mb-2">⚠️ Error Handling & Retries</h2>
        <ul className="list-disc pl-6 text-sm text-gray-700 mb-4">
          <li><strong>JWT expiration</strong> → re-register agent; retry <code>client.autodial</code></li>
          <li><strong>RATE_LIMIT_EXCEEDED</strong> / <strong>MAX_SIP_CONCURRENCY_LIMIT</strong> → exponential backoff (1s, 2s, 4s, cap at 30s)</li>
          <li><strong>TARGET_VALIDATION_FAILED</strong> → validate E.164 and retry</li>
          <li><strong>Network/500</strong> → retry with jitter</li>
        </ul>

        <h2 className="text-lg font-semibold text-gray-900 mt-6 mb-2">💡 Best Practices</h2>
        <ul className="list-disc pl-6 text-sm text-gray-700 mb-4">
          <li>Always include a unique <code>metaId</code> for reconciliation</li>
          <li>Render "dialing music" expectation in UI until <code>call.picked</code>/<code>autodialer.completed</code></li>
          <li>Stop showing "dialing" on <code>autodialer.completed</code> if <code>finalStatus</code> is not <code>CONNECTED</code></li>
          <li>Log all events with timestamps and <code>metaId</code></li>
        </ul>

        <h2 className="text-lg font-semibold text-gray-900 mt-6 mb-2">❓ FAQ</h2>
        <ul className="list-disc pl-6 text-sm text-gray-700 mb-4">
          <li><strong>Do you dial sequentially or in parallel?</strong> Parallel; first answer wins.</li>
          <li><strong>What happens to other legs once one answers?</strong> They are canceled.</li>
          <li><strong>Is there recording?</strong> Same as normal calls; see <code>call.ended.recordingUrl</code>.</li>
          <li><strong>How do I know it's done if nobody answers?</strong> Listen for <code>autodialer.completed</code> with <code>finalStatus: NO_ANSWER</code>.</li>
        </ul>

        <h2 className="text-lg font-semibold text-gray-900 mt-6 mb-2">🔧 Technical Details</h2>
        <p className="mb-4">HTTP endpoint: <code>POST /dialer/targets</code></p>
        
        <h3 className="font-semibold mt-4 mb-2">Headers</h3>
        <ul className="list-disc pl-6 text-sm text-gray-700 mb-4">
          <li><code>Authorization: Bearer &lt;agentCreds.jwt&gt;</code></li>
          <li><code>X-Taar-Version: &lt;__APP_VERSION__&gt;</code></li>
        </ul>

        <h3 className="font-semibold mt-4 mb-2">HTTP Status Codes</h3>
        <ul className="list-disc pl-6 text-sm text-gray-700 mb-4">
          <li><strong>200</strong>: Happy path</li>
          <li><strong>401</strong>: Invalid credentials</li>
          <li><strong>403</strong>: Not allowed</li>
          <li><strong>500</strong>: Server error</li>
        </ul>
      </div>
    </main>
  </div>
);

export default Autodialer; 